import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CreditCard, HelpCircle, MapPin, MessageCircle, PackageCheck, ShieldCheck, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { backendGet } from '../lib/backendClient';
import './Help.css';

const HELP_TOPICS = [
  {
    icon: PackageCheck,
    title: 'Como fazer um pedido',
    text: 'Escolha uma loja, adicione itens ao carrinho e confirme seu pedido no checkout.',
  },
  {
    icon: MapPin,
    title: 'Acompanhar entrega',
    text: 'Acesse "Meus pedidos" para ver o status e o rastreio da entrega em tempo real.',
  },
  {
    icon: CreditCard,
    title: 'Pagamentos',
    text: 'Você pode pagar via PIX, cartão ou dinheiro, conforme opções da loja.',
  },
  {
    icon: User,
    title: 'Conta e perfil',
    text: 'Atualize dados, endereço e preferências na área de perfil do cliente.',
  },
  {
    icon: ShieldCheck,
    title: 'Segurança',
    text: 'Nunca compartilhe seus códigos de confirmação com terceiros.',
  },
  {
    icon: HelpCircle,
    title: 'Problemas no pedido',
    text: 'Use o suporte do app para reportar atraso, item faltante ou cobrança indevida.',
  },
];

export default function Help() {
  const navigate = useNavigate();
  const [supportPhone, setSupportPhone] = useState('44999793369');

  useEffect(() => {
    let mounted = true;
    async function loadSupportContact() {
      try {
        const data = await backendGet('/api/v2/admin/public/support-contact', { skipAuth: true });
        if (!mounted) return;
        const raw = data?.default_whatsapp || data?.whatsapp_numbers?.[0];
        const digits = String(raw || '').replace(/\D/g, '');
        if (digits.length >= 10) {
          setSupportPhone(digits);
        }
      } catch {
        // fallback keeps test number when setting is unavailable
      }
    }
    loadSupportContact();
    return () => { mounted = false; };
  }, []);

  const supportWhatsappLink = useMemo(() => {
    const digits = String(supportPhone || '').replace(/\D/g, '');
    if (!digits) return '#';
    const full = digits.startsWith('55') ? digits : `55${digits}`;
    return `https://wa.me/${full}`;
  }, [supportPhone]);

  return (
    <div className="help-page">
      <div className="help-container">
        <button type="button" className="help-back-button" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} />
          Voltar
        </button>

        <header className="help-header">
          <h1>Central de Ajuda</h1>
          <p>Encontre respostas rápidas para pedidos, entrega, pagamento e conta.</p>
        </header>

        <section className="help-grid" aria-label="Topicos de ajuda">
          {HELP_TOPICS.map((topic) => {
            const Icon = topic.icon;
            return (
              <article key={topic.title} className="help-card">
                <div className="help-card-icon">
                  <Icon size={18} />
                </div>
                <h2>{topic.title}</h2>
                <p>{topic.text}</p>
              </article>
            );
          })}
        </section>

        <section className="help-support-card" aria-label="Falar com atendente">
          <div className="help-support-icon">
            <MessageCircle size={18} />
          </div>
          <div className="help-support-content">
            <h2>Falar com atendente Correfood</h2>
            <p>Se precisar de suporte imediato, fale direto com nosso atendimento pelo WhatsApp.</p>
            <a
              href={supportWhatsappLink}
              target="_blank"
              rel="noreferrer"
              className="help-support-link"
            >
              Conversar agora com um atendente
            </a>
          </div>
        </section>

        <section className="help-faq" aria-label="Perguntas frequentes">
          <h2>Perguntas frequentes</h2>
          <details>
            <summary>Meu pedido atrasou. O que fazer?</summary>
            <p>Abra "Meus pedidos", selecione o pedido e acompanhe o status. Se necessário, acione o suporte.</p>
          </details>
          <details>
            <summary>Posso alterar meu endereço depois do pedido?</summary>
            <p>Após confirmação, a alteração pode não ser possível. Confira os dados antes de finalizar o checkout.</p>
          </details>
          <details>
            <summary>Como recuperar acesso à conta?</summary>
            <p>Use a tela de login e siga o fluxo de recuperação de acesso com seu e-mail cadastrado.</p>
          </details>
          <details>
            <summary>Extorno</summary>
            <p>
              Em caso de cobrança indevida ou cancelamento elegível, o valor pode ser devolvido conforme o meio de
              pagamento e as regras da loja.
            </p>
          </details>
        </section>
      </div>
    </div>
  );
}
